import { useDebouncedCallback } from "@/lib/use-debounced-callback";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

interface WeightInputProps {
  value?: number | null;
  targetWeight?: number | null;
  onChange: (value: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  showButtons?: boolean;
  autoSave?: boolean;
  autoSaveDelay?: number;
  "data-testid"?: string;
}

export function WeightInput({
  value,
  targetWeight,
  onChange,
  step = 2.5,
  min = 0,
  max = 500,
  disabled = false,
  placeholder = "kg",
  className,
  showButtons = true,
  autoSave = true,
  autoSaveDelay = 800,
  "data-testid": dataTestId,
}: WeightInputProps) {
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? "");
  const [isFocused, setIsFocused] = useState(false);
  // Ref always holds the latest typed value — avoids stale closure in handleBlur
  const latestValueRef = useRef(localValue);

  // Track the last value sent to onChange to prevent double-firing mutations
  const lastSavedValueRef = useRef<number | null>(value ?? null);

  // Sync local value with prop when not focused (e.g. external data change, day switch).
  // lastSavedValueRef is intentionally NOT updated on isFocused change to prevent a
  // re-render triggered by blur from overwriting the ref before the pending debounce fires.
  // biome-ignore lint/correctness/useExhaustiveDependencies: isFocused intentionally omitted — see comment above
  useEffect(() => {
    if (!isFocused) {
      const str = value?.toString() ?? "";
      setLocalValue(str);
      latestValueRef.current = str;
      lastSavedValueRef.current = value ?? null;
    }
  }, [value]);

  // Debounced auto-save via Pacer
  const debouncedSave = useDebouncedCallback(
    (raw: string) => {
      if (!autoSave) return;
      const numValue = raw === "" ? null : Number.parseFloat(raw);
      if (numValue !== lastSavedValueRef.current) {
        lastSavedValueRef.current = numValue;
        onChange(numValue);
      }
    },
    { wait: autoSaveDelay },
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // Replace comma with dot for mobile keyboards
      const newValue = e.target.value.replace(",", ".");

      // Allow empty, numbers, decimal point, and optional negative sign
      if (newValue === "" || /^-?\d*\.?\d*$/.test(newValue)) {
        latestValueRef.current = newValue; // sync ref first
        setLocalValue(newValue);
        if (isFocused) debouncedSave(newValue);
      }
    },
    [isFocused, debouncedSave],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Read from ref — always latest regardless of React batching
    const raw = latestValueRef.current;
    const numValue = raw === "" ? null : Number.parseFloat(raw);
    if (numValue !== null && (numValue < min || numValue > max)) {
      const clampedValue = Math.max(min, Math.min(max, numValue));
      latestValueRef.current = clampedValue.toString();
      setLocalValue(clampedValue.toString());
      if (clampedValue !== lastSavedValueRef.current) {
        lastSavedValueRef.current = clampedValue;
        onChange(clampedValue);
      }
    } else if (numValue !== lastSavedValueRef.current) {
      lastSavedValueRef.current = numValue;
      onChange(numValue);
    }
  }, [min, max, onChange]);

  const increment = useCallback(() => {
    const current = localValue === "" ? 0 : Number.parseFloat(localValue);
    const newValue = Math.min(max, current + step);
    setLocalValue(newValue.toString());
    lastSavedValueRef.current = newValue;
    onChange(newValue);
  }, [localValue, max, step, onChange]);

  const decrement = useCallback(() => {
    const current = localValue === "" ? 0 : Number.parseFloat(localValue);
    const newValue = Math.max(min, current - step);
    setLocalValue(newValue.toString());
    lastSavedValueRef.current = newValue;
    onChange(newValue);
  }, [localValue, min, step, onChange]);

  // Show difference from target
  const numValue = localValue === "" ? null : Number.parseFloat(localValue);
  const diff =
    numValue !== null && targetWeight != null ? numValue - targetWeight : null;

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {showButtons && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={decrement}
          disabled={disabled}
        >
          <Minus className="h-3 w-3" />
        </Button>
      )}

      <div className="relative flex-1">
        <Input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={dataTestId}
          className={cn(
            "h-9 w-full min-w-[60px] pr-6 text-center text-sm",
            diff !== null &&
              diff > 0 &&
              "border-green-500/50 dark:border-green-400/50",
            diff !== null &&
              diff < 0 &&
              "border-orange-500/50 dark:border-orange-400/50",
          )}
        />
        <span className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-2 text-muted-foreground text-xs">
          kg
        </span>
      </div>

      {showButtons && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={increment}
          disabled={disabled}
        >
          <Plus className="h-3 w-3" />
        </Button>
      )}

      {/* Difference indicator */}
      {diff !== null && diff !== 0 && (
        <span
          className={cn(
            "ml-1 font-medium text-xs",
            diff > 0
              ? "text-green-600 dark:text-green-400"
              : "text-orange-600 dark:text-orange-400",
          )}
        >
          {diff > 0 ? "+" : ""}
          {diff}
        </span>
      )}
    </div>
  );
}
